import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize    = 'sm' | 'md';

@Component({
  selector: 'app-button, button[app-button]',
  standalone: true,
  imports: [CommonModule],
  template: `
    <button
      [type]="type"
      [disabled]="disabled || loading"
      (click)="onButtonClick($event)"
      class="inline-flex items-center justify-center gap-1.5 font-semibold transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed backdrop-blur-md w-full"
      [class.px-4]="size === 'md'"
      [class.py-2]="size === 'md'"
      [class.text-sm]="size === 'md'"
      [class.rounded-xl]="size === 'md'"
      [class.px-3]="size === 'sm'"
      [class.py-1.5]="size === 'sm'"
      [class.text-xs]="size === 'sm'"
      [class.rounded-lg]="size === 'sm'"
      [class.bg-gradient-to-br]="true"
      [class.from-cyan-500\/18]="variant === 'primary'"
      [class.to-blue-500\/12]="variant === 'primary'"
      [class.border]="true"
      [class.border-cyan-400\/35]="variant === 'primary'"
      [class.text-cyan-300]="variant === 'primary'"
      [class.shadow-\[inset_0_1px_0_rgba(255,255,255,0.06),0_2px_8px_rgba(6,182,212,0.15)\]]="variant === 'primary'"
      [class.hover:from-cyan-500\/28]="variant === 'primary'"
      [class.hover:to-blue-500\/20]="variant === 'primary'"
      [class.hover:border-cyan-400\/50]="variant === 'primary'"
      [class.hover:text-cyan-200]="variant === 'primary'"
      [class.from-blue-500\/15]="variant === 'secondary'"
      [class.to-indigo-500\/10]="variant === 'secondary'"
      [class.border-blue-400\/30]="variant === 'secondary'"
      [class.text-blue-300]="variant === 'secondary'"
      [class.hover:from-blue-500\/25]="variant === 'secondary'"
      [class.hover:border-blue-400\/45]="variant === 'secondary'"
      [class.hover:text-blue-200]="variant === 'secondary'"
      [class.from-red-500\/15]="variant === 'danger'"
      [class.to-red-600\/8]="variant === 'danger'"
      [class.border-red-400\/30]="variant === 'danger'"
      [class.text-red-300]="variant === 'danger'"
      [class.hover:from-red-500\/25]="variant === 'danger'"
      [class.hover:border-red-400\/45]="variant === 'danger'"
      [class.hover:text-red-200]="variant === 'danger'"
      [class.bg-white\/4]="variant === 'ghost'"
      [class.border-white\/10]="variant === 'ghost'"
      [class.text-\[var(--color-text-muted)\]]="variant === 'ghost'"
      [class.hover:bg-white\/8]="variant === 'ghost'"
      [class.hover:text-\[var(--color-text-secondary)\]]="variant === 'ghost'"
    >
      @if (loading) {
        <span class="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
      }
      <ng-content />
    </button>
  `,
})
export class ButtonComponent {
  @Input() variant: ButtonVariant = 'primary';
  @Input() size: ButtonSize = 'md';
  @Input() loading = false;
  @Input() disabled = false;
  @Input() type: 'button' | 'submit' = 'button';
  @Output() clicked = new EventEmitter<MouseEvent>();

  onButtonClick(event: MouseEvent): void {
    if (this.type === 'submit') {
      // For submit buttons, let the form handle the submission natively
      return;
    }
    // Only emit click event for non-submit buttons
    this.clicked.emit(event);
  }
}
